import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { Button, EmptyState } from "@/components/common";
import { formatRelativeTime, formatDateTimeAUS } from "@/utils/dateTime";
import { NOTIFICATION_TYPE_COLORS, NOTIFICATION_TYPE_LABELS, } from "@/types/notification.types";
import styles from "./NotificationsSection.module.css";
const DocumentIcon = () => (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
function getTypeColor(type) {
    return (NOTIFICATION_TYPE_COLORS[type] ??
        "blue");
}
function getTypeLabel(type) {
    return (NOTIFICATION_TYPE_LABELS[type] ??
        String(type));
}
function getNotificationNavigatePath(notification, pathname) {
    const ref = notification.referenceData;
    if (!ref)
        return null;
    const isClient = pathname.startsWith("/client");
    const basePath = pathname.startsWith("/staff")
        ? "/staff"
        : pathname.startsWith("/admin")
            ? "/admin"
            : pathname.startsWith("/surveyor") || pathname.startsWith("/contractor")
                ? "/staff"
                : "/admin";
    if (ref.jobId) {
        return isClient
            ? `/client/jobs/${ref.jobId}`
            : `${basePath}/jobs/${ref.jobId}/edit`;
    }
    if (ref.quoteId) {
        return isClient
            ? `/client/quotes/${ref.quoteId}/report`
            : `${basePath}/estimating/${ref.quoteId}/edit`;
    }
    return null;
}
export const NotificationsSection = ({ notifications, onNotificationClick, fromAdminHome = false, fromClientHome = false, onLoadMore, hasMore = false, loadingMore = false, }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const scrollContainerRef = useRef(null);
    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!onLoadMore || !hasMore || loadingMore)
            return;
        const root = scrollContainerRef.current;
        const sentinel = sentinelRef.current;
        if (!root || !sentinel)
            return;
        const observer = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (entry?.isIntersecting && hasMore && !loadingMore) {
                onLoadMore();
            }
        }, { root, rootMargin: "100px", threshold: 0 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [onLoadMore, hasMore, loadingMore]);
    const handleItemClick = useCallback((notification) => {
        const path = getNotificationNavigatePath(notification, location.pathname);
        if (path)
            navigate(path);
    }, [navigate, location.pathname]);
    return (_jsxs("div", { className: fromAdminHome || fromClientHome
            ? `${styles.wrapper} ${styles.wrapperHome}`
            : styles.wrapper, children: [_jsx("div", { className: styles.notificationsHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "NOTIFICATIONS" }) }), _jsx("div", { className: fromAdminHome
                    ? `${styles.contentCard} ${styles.contentCardAdminHome}`
                    : fromClientHome
                        ? `${styles.contentCard} ${styles.contentCardClientHome}`
                        : styles.contentCard, children: _jsxs("div", { ref: scrollContainerRef, className: fromAdminHome
                        ? `${styles.notificationsList} ${styles.notificationsListAdminHome}`
                        : fromClientHome
                            ? `${styles.notificationsList} ${styles.notificationsListClientHome}`
                            : styles.notificationsList, children: [notifications.length === 0 ? (_jsx(EmptyState, { image: "/images/empty-state-notification.png", heading: "No new notifications", text: "You'll see important updates here as they happen." })) : (notifications.map((notification) => {
                            const colorVariant = getTypeColor(notification.type);
                            const iconVariantClass = styles[`notificationIcon${colorVariant.charAt(0).toUpperCase()}${colorVariant.slice(1)}`];
                            const itemVariantClass = styles[`notificationItem${colorVariant.charAt(0).toUpperCase()}${colorVariant.slice(1)}`];
                            const isClickable = !!notification.referenceData?.jobId ||
                                !!notification.referenceData?.quoteId;
                            const isAlreadyActioned = !!notification.actionedByDate ||
                                !!(notification.isRead && notification.readAt);
                            return (_jsx(Tooltip, { title: notification.message || "", placement: "top", enterDelay: 300, leaveDelay: 0, disableHoverListener: !notification.message, children: _jsxs("div", { className: `${styles.notificationItem} ${itemVariantClass ?? ""} ${isClickable ? styles.notificationItemClickable : ""}`, role: isClickable ? "button" : undefined, tabIndex: isClickable ? 0 : undefined, onClick: isClickable
                                        ? () => handleItemClick(notification)
                                        : undefined, onKeyDown: isClickable
                                        ? (e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleItemClick(notification);
                                            }
                                        }
                                        : undefined, children: [_jsx("div", { className: `${styles.notificationIcon} ${iconVariantClass ?? ""}`, children: _jsx(DocumentIcon, {}) }), _jsxs("div", { className: styles.notificationContent, children: [_jsxs("div", { className: styles.notificationHeader, children: [_jsx("span", { className: styles.notificationType, children: getTypeLabel(notification.type) }), _jsx("span", { className: styles.notificationTime, children: formatRelativeTime(notification.createdAt) })] }), _jsx("div", { className: styles.notificationTitle, children: notification.title }), _jsx("div", { className: styles.notificationText, children: notification.message }), (notification.actionedByDate ||
                                                    (notification.isRead && notification.readAt)) && (_jsx("div", { className: styles.notificationActionedInfo, children: notification.actionedByName
                                                        ? `Actioned by ${notification.actionedByName} on ${formatDateTimeAUS(notification.actionedByDate ?? notification.readAt)}`
                                                        : `Actioned on ${formatDateTimeAUS(notification.actionedByDate ?? notification.readAt)}` })), onNotificationClick && !isAlreadyActioned && (_jsx("div", { className: styles.notificationFooter, children: _jsx(Button, { variant: "secondary", className: styles.actionedButton, onClick: (e) => {
                                                            e.stopPropagation();
                                                            onNotificationClick(notification);
                                                        }, children: "Actioned" }) }))] })] }) }, notification.id));
                        })), notifications.length > 0 && onLoadMore && hasMore && (_jsx("div", { ref: sentinelRef, className: styles.loadMoreSentinel })), loadingMore && _jsx("div", { className: styles.loadingMore, children: "Loading..." })] }) })] }));
};
